// Dashboard login client — talks to backend/routers/auth.py.
// Unrelated to the X-API-Key used by the /chat endpoints (see api/chat.ts).

import { config } from "../config";
import type { AuthUser } from "../types";

const TOKEN_STORAGE_KEY = "gridscout_session_token";

export class AuthError extends Error {}

export function getStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function storeToken(token: string) {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch {
    // ignore (private browsing / storage disabled)
  }
}

export function clearStoredToken() {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export async function login(username: string, password: string): Promise<{ token: string; user: AuthUser }> {
  const res = await fetch(`${config.apiBase}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });

  if (res.status === 401) {
    throw new AuthError("Invalid username or password.");
  }

  if (res.status === 429) {
    throw new AuthError("Too many attempts. Please wait a moment and try again.");
  }

  if (!res.ok) {
    throw new AuthError("Unable to sign in right now. Please try again.");
  }

  return res.json();
}

/** Validates a stored token against the backend. Returns the user, or null if invalid/expired. */
export async function fetchCurrentUser(token: string): Promise<AuthUser | null> {
  try {
    const res = await fetch(`${config.apiBase}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) return null;

    const body = await res.json();
    return body.user as AuthUser;
  } catch {
    return null;
  }
}
